import sortBy from 'lodash/sortBy';
import { createSelector } from 'reselect';

import { RootState } from 'src/config/rootStore';
import { CoachNode } from 'src/generated/webapp_gql';
import { ProviderType } from 'src/modules/user/models/user';

const sortByMatchScore = (providers: CoachNode[]) => sortBy(providers, ['matchScore']).reverse();

export const getMyProviderAssignments = (state: RootState) => state.providers.myProviders;

export const getAllAvailableCoaches = (state: RootState) => state.providers.availableCoaches;

export const getAllAvailableTherapists = (state: RootState) => state.providers.availableTherapists;

export const getAllAvailableProviders = createSelector(
    getAllAvailableCoaches,
    getAllAvailableTherapists,
    (coaches, therapists) => [...coaches, ...therapists],
);

export const getMyProviders = createSelector(
    getMyProviderAssignments,
    (myCoachAssignments): CoachNode[] => {
        const providers = myCoachAssignments
            .filter(({ active }) => active === true)
            .map(({ coach }) => coach as CoachNode)
            .filter(provider => provider.isInAppProvider === true);

        return sortByMatchScore(providers);
    },
);

export const getMyProvidersWithPammFilter = createSelector(
    getMyProviderAssignments,
    (myCoachAssignments): CoachNode[] => {
        const providers = myCoachAssignments
            .filter(({ active }) => active === true)
            .filter(({ displayOnSchedule }) => displayOnSchedule === true)
            .map(({ coach }) => coach as CoachNode)
            .filter(provider => provider.isInAppProvider === true);

        return sortByMatchScore(providers);
    },
);

// returns only therapists that are not marked as in-app provider
export const getOutOfTheAppTherapists = createSelector(
    getMyProviderAssignments,
    myCoachAssignments =>
        myCoachAssignments
            .filter(({ active }) => active === true)
            .map(({ coach }) => coach as CoachNode)
            .filter(
                provider =>
                    provider.isInAppProvider !== true &&
                    provider.providerType === ProviderType.THERAPIST,
            ),
);

export const getConversationByProviderUuid = (providerUuid: string) =>
    createSelector(
        getMyProviderAssignments,
        myCoachAssignments =>
            myCoachAssignments.find(({ coach }) => coach?.uuid === providerUuid)?.conversation,
    );

export const getMyCoaches = createSelector(getMyProviders, providers =>
    providers.filter(provider => provider.providerType === ProviderType.COACH),
);

export const getMyTherapists = createSelector(getMyProviders, providers =>
    providers.filter(provider => provider.providerType === ProviderType.THERAPIST),
);

export const getProviderByUuid = (providerUuid: string) =>
    createSelector(getMyProviders, providers =>
        providers.find(provider => provider.uuid === providerUuid),
    );

export const getMyProvidersUuid = createSelector(getMyProviders, providers =>
    providers.map(provider => provider.uuid),
);

export const isUserProvidersFetchCompleted = (state: RootState) => state.providers.fetchCompleted;

export const getProvidersSortedByMatchScore = createSelector(
    getAllAvailableProviders,
    sortByMatchScore,
);

export const getAvailableCoachesSortedByMatchScore = createSelector(
    getAllAvailableCoaches,
    sortByMatchScore,
);

export const getAvailableTherapistsSortedByMatchScore = createSelector(
    getAllAvailableTherapists,
    sortByMatchScore,
);

export const getProvidersAvailableToChoose = createSelector(
    getProvidersSortedByMatchScore,
    getMyProvidersUuid,
    (providers, myProvidersUuid) =>
        providers.filter(provider => !myProvidersUuid.includes(provider.uuid)),
);

export const getCoachesAvailableToChoose = createSelector(
    getAvailableCoachesSortedByMatchScore,
    getMyProvidersUuid,
    (providers, myProvidersUuid) =>
        providers.filter(provider => !myProvidersUuid.includes(provider.uuid)),
);

export const getTherapistsAvailableToChoose = createSelector(
    getAvailableTherapistsSortedByMatchScore,
    getMyProvidersUuid,
    (providers, myProvidersUuid) =>
        providers.filter(provider => !myProvidersUuid.includes(provider.uuid)),
);

export const isProviderAssignmentStillActive = (providerUuid: string) =>
    createSelector(getMyProviderAssignments, assignments => {
        const assignment = assignments.find(({ coach }) => coach?.uuid === providerUuid);

        return assignment?.active;
    });
